





















import mixins from 'vue-typed-mixins'
import { mapActions, mapGetters } from 'vuex'
import { RawLocation } from 'vue-router'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import { AxiosPromise, AxiosResponse } from 'axios'
import { RouteNames } from '@/router'
import WindowResizeListener from '@/abstract/WindowResizeListener'
import Icon from '@/components/Icon/Icon.vue'
import { SCRIPTS__MAKE_ACTION } from '@/store/actions'
import { UserScope } from '@/models/users'
import {
  ScriptVersion,
  ScriptUnit,
  ScriptActionPayload,
} from '@/models/scripts'
import {
  ScriptAction,
  ScriptStatus,
  ScriptPublishStatus,
  ScriptPublishStatusMessages,
} from '@/constants/scripts'

interface Methods {
  [SCRIPTS__MAKE_ACTION](data: ScriptActionPayload): AxiosPromise
  handleToggleScriptPublish: () => void
}

interface Computed {
  canDoScope: (scope: UserScope) => boolean
  currentInstanceId?: string
  currentInstance?: ScriptVersion
  status: ScriptStatus
  publishBtnClass: string
  publishBtnText: string
  publishIcon: string
  publishIconColor: string
  schemeLink: RawLocation
  scriptListLink: RawLocation
  canEditScript: boolean
  canPublishScript: boolean
}

interface Props {
  scriptId: string
  scriptItem: ScriptUnit
  instanceId?: string
  isShowLinks?: boolean
}

export default mixins(WindowResizeListener).extend<
  {},
  Methods,
  Computed,
  Props
>({
  components: {
    Icon,
  },
  props: {
    scriptItem: {
      required: true,
    },
    scriptId: {
      required: true,
    },
    instanceId: {
      default: () => undefined,
    },
    isShowLinks: {
      default: () => true,
    },
  },
  computed: {
    ...mapGetters({
      canDoScope: 'canDoScope',
    }),
    currentInstanceId(): string | undefined {
      return this.instanceId || this.scriptItem?.instance?.id
    },
    currentInstance(): ScriptVersion | undefined {
      if (!this.currentInstanceId) {
        return
      }
      return this.scriptItem.versions?.find(
        v => v.instance.id === this.currentInstanceId,
      )
    },
    status(): ScriptStatus {
      if (this.instanceId) {
        return this.currentInstance?.active
          ? ScriptStatus.Publish
          : ScriptStatus.Unpublish
      }
      return this.scriptItem?.status || ScriptStatus.Unpublish
    },
    publishBtnClass(): string {
      return this.status === ScriptStatus.Publish ? '-red' : '-green'
    },
    publishBtnText(): string {
      return this.status === ScriptStatus.Publish
        ? this.$tc('script.actions.unpublish')
        : this.$tc('common.publish')
    },
    publishIcon(): string {
      return this.status === ScriptStatus.Publish
        ? 'zmdi-layers-off'
        : 'zmdi-layers'
    },
    publishIconColor(): string {
      return this.status === ScriptStatus.Publish ? 'red' : 'purple'
    },
    schemeLink(): RawLocation {
      return {
        name: RouteNames.ScriptSchemePreview,
        params: {
          scriptId: this.scriptId,
          instanceId: this.instanceId as string,
        },
      }
    },
    scriptListLink(): RawLocation {
      return {
        name: this.scriptItem.sample
          ? RouteNames.ScriptTemplates
          : RouteNames.ScriptsPage,
      }
    },
    canEditScript(): boolean {
      return this.canDoScope(UserScope.ScriptsEdit)
    },
    canPublishScript(): boolean {
      return (
        !this.scriptItem.sample && this.canDoScope(UserScope.ScriptsPublish)
      )
    },
  },
  methods: {
    ...mapActions([SCRIPTS__MAKE_ACTION]),
    handleToggleScriptPublish(): void {
      let text = ''

      const action =
        this.status === ScriptStatus.Publish
          ? ScriptAction.UnPublish
          : ScriptAction.Publish

      if (action === ScriptAction.UnPublish) {
        text = this.$tc('script.actions.unpublishSuccess')
      }

      if (this.currentInstanceId && !this.$route.params.instanceId) {
        this.$router.replace({
          params: {
            ...this.$route.params,
            instanceId: this.currentInstanceId,
          },
        })
      }

      this[SCRIPTS__MAKE_ACTION]({
        action,
        id: this.scriptId,
        instance: this.instanceId,
      }).then(
        (
          response: AxiosResponse<{ publish: ScriptPublishStatus } | boolean>,
        ) => {
          let type = 'info'
          if (typeof response.data !== 'boolean' && response.data.publish) {
            text = ScriptPublishStatusMessages[response.data.publish]
            if (
              [
                ScriptPublishStatus.Not,
                ScriptPublishStatus.NotFound,
                ScriptPublishStatus.NotVersioned,
              ].includes(response.data.publish)
            ) {
              type = 'warning'
            }
          }
          this.$store.dispatch(ADD_TOAST_MESSAGE, {
            text,
            type,
          })
        },
      )
    },
  },
})
